import type { Point, Size } from '@ikegps/core-api';

import {
	CollectionModelDrawable,
	CollectionModelDrawableLabel,
	CollectionModelDrawableLine,
	CollectionModelDrawableMarker,
	CollectionModelDrawableMarkerOffset,
	CollectionModelDrawableVerticalSeparation,
	type DrawableWithLabel,
	InsightMarkerDetectionClass,
} from '../../model';
import { IkeFetchableRaceError } from '../../rest/fetchable';
import type { SVGRenderOptions } from '../draw/svg/svg.render';
import { m } from '../mithril';
import { ClassNames, StyleConfig } from '../theme';
import { IkeDrawLabel, SVGUtil } from './util.draw.svg';

function getComputedTextLengthSafe(textEl) {
	try {
		return textEl.getComputedTextLength();
	} catch (_e) {
		return 0;
	}
}

const LABEL_ACCEPT_AND_DELETE_RADIUS = 8;
const DEFAULT_CHAR_WIDTH = 6;

const ELEMENT_NODE = 1;

// Character width mapping for Roboto Condensed at 14px font size
const ROBOTO_CONDENSED_CHAR_WIDTHS: { [char: string]: number } = {
	0: 7.8,
	1: 7.8,
	2: 7.8,
	3: 7.8,
	4: 7.8,
	5: 7.8,
	6: 7.8,
	7: 7.8,
	8: 7.8,
	9: 7.8,
	A: 8.1,
	B: 7.9,
	C: 8.0,
	D: 8.3,
	E: 7.2,
	F: 6.8,
	G: 8.5,
	H: 8.2,
	I: 3.2,
	J: 5.8,
	K: 7.7,
	L: 6.5,
	M: 9.8,
	N: 8.2,
	O: 8.7,
	P: 7.5,
	Q: 8.7,
	R: 7.8,
	S: 7.5,
	T: 7.0,
	U: 8.0,
	V: 7.8,
	W: 11.2,
	X: 7.5,
	Y: 7.3,
	Z: 7.2,
	a: 6.8,
	b: 7.2,
	c: 6.5,
	d: 7.2,
	e: 6.8,
	f: 4.0,
	g: 7.2,
	h: 7.0,
	i: 3.0,
	j: 3.0,
	k: 6.3,
	l: 3.0,
	m: 10.5,
	n: 7.0,
	o: 7.0,
	p: 7.2,
	q: 7.2,
	r: 4.3,
	s: 6.2,
	t: 4.0,
	u: 7.0,
	v: 6.2,
	w: 9.0,
	x: 6.0,
	y: 6.2,
	z: 6.0,
	' ': 3.5,
	'.': 3.0,
	',': 3.0,
	'!': 3.5,
	'?': 6.5,
	':': 3.0,
	';': 3.0,
	"'": 2.3,
	'"': 4.5,
	'-': 4.2,
	_: 5.5,
	'(': 4.2,
	')': 4.2,
	'[': 3.8,
	']': 3.8,
	'{': 4.5,
	'}': 4.5,
	'/': 5.2,
	'\\': 5.2,
	'|': 3.2,
	'@': 11.5,
	'#': 8.0,
	$: 7.8,
	'%': 11.0,
	'^': 6.5,
	'&': 8.5,
	'*': 5.5,
	'+': 7.3,
	'=': 7.3,
	'<': 7.3,
	'>': 7.3,
	'~': 7.8,
	'`': 4.0,
	'…': 7.0,
};

function getCharWidth(char: string): number {
	return ROBOTO_CONDENSED_CHAR_WIDTHS[char] || 6.5; // Default width for unmapped characters
}

/**
 * Calculates the total width of text using Roboto Condensed character widths
 */
function calculateTextWidth(text: string, fontSize: number = 14): number {
	const scale = fontSize / 14;
	let width = 0;
	for (let i = 0; i < text.length; i++) {
		const char = text.charAt(i);
		width += getCharWidth(char) * scale;
	}
	return width;
}

/**
 * Truncates text for PDF export using Roboto Condensed character widths
 */
function truncateTextForPDF(text: string, maxWidth: number, fontSize: number = 14): string {
	const ellipsisWidth = calculateTextWidth('…', fontSize);
	const availableWidth = maxWidth - ellipsisWidth;

	if (calculateTextWidth(text, fontSize) <= maxWidth) {
		return text;
	}

	let truncatedText = '';
	let currentWidth = 0;

	for (let i = 0; i < text.length; i++) {
		const char = text.charAt(i); // Use charAt instead of bracket notation
		const charWidth = getCharWidth(char) * (fontSize / 14);
		if (currentWidth + charWidth > availableWidth) {
			break;
		}
		truncatedText += char;
		currentWidth += charWidth;
	}

	return truncatedText + '…';
}

/** Line length to verify that it is not too long, in characters */
const ELLIPSIS_LINE_LENGTH = 25;
/** Length of a line that will have to be ...'d  */
const ELLIPSIS_LINE_MAX_LENGTH = 35;

export const Style = {
	[ClassNames.label.text]: {
		'font-size': StyleConfig.FONT_SIZE + 'px',
		'user-select': 'none',
		'pointer-events': 'none',
	},
	[ClassNames.label.rect.container]: {
		fill: StyleConfig.LABEL.FILL,
		stroke: 'none',
		'stroke-width': 0,
	},

	[ClassNames.label.rect.isHighlighted]: {
		fill: StyleConfig.LABEL.FILL_HIGHLIGHTED,
	},
};

export const LABEL_SIZE: Size = {
	height: 5,
	width: 210,
};

export class LabelSVGDraw {
	static Size = LABEL_SIZE;

	static view(label: CollectionModelDrawableLabel, options: SVGRenderOptions) {
		const labelChildren: any[] = [];

		const parent = label.parent;

		let nodeId = label.id;
		let labelHeight = LabelSVGDraw.height(1);
		let isParentSelected = false;
		let isDeleteShown = false;
		let isAcceptShown = false;
		let spanColors: string[] | null = null;
		const backgroundRectAttrs: any = {};

		// Label text for each class of parent
		if (
			parent != null &&
			(CollectionModelDrawableMarker.isMarker(parent) || CollectionModelDrawableMarkerOffset.isBaseOffset(parent))
		) {
			const text = options.draw.getLabelText(parent.id, options);

			spanColors = options.useColorCoding ? options.draw.getSpanColors(parent.id) : null;

			labelHeight = LabelSVGDraw.height(text.length);
			labelChildren.push(LabelSVGDraw.textLines(label, options, text));
			nodeId = parent.id;

			// If the label is for a base or base offset insight marker, set the text accordingly.
			if (parent instanceof CollectionModelDrawableMarker) {
				// if the detection class is zero offset, show the base selection text
				// if the detection class is an offset class other than zero_offset, show the base offset selection text
				// if its neither then leave it as null
				const baseSelectionText =
					parent.insight?.detection_class === InsightMarkerDetectionClass.ZeroOffset
						? 'Select to Set Base'
						: parent.isInsightBaseOffsetDetectionClass
							? 'Select to Set Base Offset'
							: null;

				if (baseSelectionText && !parent.collectionHasBase) {
					labelChildren.push(LabelSVGDraw.textLines(label, options, [baseSelectionText]));
				}
			}

			isDeleteShown =
				options.isInteractive && options.isEditable && parent.isDeletable && options.draw.state.tool == null;

			isAcceptShown = isDeleteShown && !!options.isInsightMarkersVisible && label.isApprovableInsightDrawable();
		} else if (parent != null && CollectionModelDrawableLine.isLine(parent)) {
			nodeId = parent.id;
			const text = options.draw.getLabelText(parent.id, options);
			labelHeight = LabelSVGDraw.height(text.length);

			const selectedId = options.draw.state.selectedId;
			isParentSelected = selectedId === nodeId || selectedId === parent.start.id || selectedId === parent.end.id;

			labelChildren.push(LabelSVGDraw.textLines(label, options, text));

			isDeleteShown =
				options.isInteractive && options.isEditable && parent.isDeletable && options.draw.state.tool == null;

			isAcceptShown = isDeleteShown && !!options.isInsightMarkersVisible && label.isInsightMeasurement;
		} else if (parent != null && parent instanceof CollectionModelDrawableVerticalSeparation) {
			backgroundRectAttrs.stroke = parent.clearanceThresholdInMeters ? (parent.isInClearance ? 'lime' : 'red') : 'none';
			backgroundRectAttrs['stroke-width'] = StyleConfig.STROKE_WIDTH * options.scale;
			labelChildren.push(LabelSVGDraw.textLines(label, options, label.getDisplayText(options)));
		} else {
			labelChildren.push(LabelSVGDraw.textLines(label, options, label.text));
		}

		const isSelected = options.draw.state.selectedId === nodeId || isParentSelected;
		const isHighlighted = options.draw.state.hoverId === nodeId;

		if (isDeleteShown && (isSelected || isHighlighted)) {
			labelChildren.push(LabelSVGDraw.deleteButton(options));
		}

		if (isAcceptShown && (isSelected || isHighlighted)) {
			labelChildren.push(LabelSVGDraw.acceptButton(options));
		}

		const classNames = [ClassNames.label.container, isSelected ? ClassNames.isSelected : null].join(' ');

		const rectClassNames = [
			ClassNames.label.rect.container,
			isSelected ? ClassNames.label.rect.isSelected : null,
			isHighlighted ? ClassNames.label.rect.isHighlighted : null,
			options.isInteractive ? ClassNames.isMoveable : null,
		].join(' ');

		var fill = StyleConfig.LABEL.FILL;
		if (options.isInsightMarkersVisible && label.isInsightMeasurement) {
			fill = StyleConfig.LABEL.FILL_INSIGHT;
		} else if (isHighlighted) {
			fill = StyleConfig.LABEL.FILL_HIGHLIGHTED;
		} else if (isSelected) {
			fill = StyleConfig.LABEL.FILL_SELECTED;
		}

		if (spanColors) {
			labelChildren.push(LabelSVGDraw.rectSpansColors(spanColors, labelHeight, isHighlighted, options));
		}

		const width = options.scale * LABEL_SIZE.width;
		const height = options.scale * labelHeight;

		const backgroundRect = m('rect', {
			className: rectClassNames,
			x: '0',
			y: '0',
			width,
			height,
			fill,
			'fill-opacity': isHighlighted ? 0.9 : 0.75,
			'c-Draw-Label': IkeDrawLabel.LABEL,
			'c-Draw-draggable': 'true',
			...backgroundRectAttrs,
		});

		const labelBox = m(
			'g',
			{
				transform: `translate(${label.x}, ${label.y})`,
			},
			[[backgroundRect, ...labelChildren]]
		);

		return m(
			'g',
			{
				// key: currentState.id,
				className: classNames,
				'c-Draw-node-id': nodeId,
				'c-Draw-Label': IkeDrawLabel.LABEL,
			},
			[labelBox, ...LabelSVGDraw.DrawConnectingLineToParent(label, parent, options)]
		);
	}

	private static DrawConnectingLineToParent(
		label: CollectionModelDrawableLabel,
		parent: CollectionModelDrawable & DrawableWithLabel,
		options: SVGRenderOptions
	) {
		try {
			const parentConnectionPoint = parent.getLabelConnectionPoint(options);

			// If the label is on the left, set the label connection point to be the middle of the right side of the label.
			// Otherwise, set it to the middle of the left side of the label.
			const labelConnectionPoint: Point = {
				x: label.isOnLeftSide()
					? label.x + options.scale * LabelSVGDraw.Size.width // Right
					: label.x + 5, // Left
				y: label.y + (options.scale * LabelSVGDraw.height(label.text.length)) / 2,
			};

			// Draw the line connecting the label to the parent.
			return [
				SVGUtil.DrawLineMain(parentConnectionPoint, labelConnectionPoint, options.scale),
				SVGUtil.DrawLineSecondary(
					parentConnectionPoint,
					labelConnectionPoint,
					options.scale,
					parent.isInsightMeasurement ? StyleConfig.STOKE_SECONDARY_INSIGHT : StyleConfig.STROKE_SECONDARY
				),
			];
		} catch (error: unknown) {
			if (error instanceof IkeFetchableRaceError) {
				options.draw.log.warn({ error }, 'Caught IkeFetchableRaceError');
				return [];
			}
			throw error;
		}
	}

	static rectSpansColors(spanColors: string[], labelHeight: number, isHighlighted: boolean, options: SVGRenderOptions) {
		const width = options.scale * LABEL_SIZE.width;
		const height = options.scale * labelHeight;

		let colorRects: any[] = [];

		spanColors.forEach((color, index) => {
			const colorRect = m('rect', {
				x: width * 0.015 * index,
				y: '0',
				width: width * 0.015,
				height,
				fill: color,
				'fill-opacity': isHighlighted ? 1 : 0.85,
			});
			colorRects.push(colorRect);
		});
		return colorRects;
	}

	static height(lineCount: number) {
		return LABEL_SIZE.height + (StyleConfig.FONT_SIZE + 0.25 * StyleConfig.FONT_SIZE) * (lineCount || 1);
	}

	static textLines(node: CollectionModelDrawableLabel, options: SVGRenderOptions, lines: string[]) {
		let longestLine = 0;
		const output: any[] = [];
		for (let i = 0; i < lines.length; i++) {
			var line = lines[i] ?? '';
			var text = LabelSVGDraw.text(node, options, line, i);
			text.attrs.y = 1 + i * 1.25 + 'em';
			longestLine = Math.max(longestLine, line.length);
			output.push(text);
		}

		const displayAsInsight = options.isInsightMarkersVisible && node.isInsightMeasurement;
		const xOffset = (LABEL_SIZE.width * options.scale) / 2;
		const attrs = {
			transform: `translate(${xOffset}, ${(-6 * options.scale) / 4})scale(${options.scale})`,
			fill:
				options.isInsightMarkersVisible && displayAsInsight ? StyleConfig.FONT_COLOR_INSIGHT : StyleConfig.FONT_COLOR,
		};
		if (options.isInteractive && longestLine > ELLIPSIS_LINE_LENGTH) {
			const ellipsisText = (el) => LabelSVGDraw.ellipsisText(el);
			attrs['oncreate'] = ellipsisText;
			attrs['onupdate'] = ellipsisText;
		}

		return m('g', attrs, output);
	}

	static text(node: CollectionModelDrawableLabel, options: SVGRenderOptions, line: string, offset: number) {
		let outputLine = line;
		// Truncate text only for PDF, for browser is done differently
		if (options.isInteractive === false) {
			const maxWidth = LABEL_SIZE.width * 0.95; // 95% of label width for padding
			outputLine = truncateTextForPDF(line, maxWidth, StyleConfig.FONT_SIZE);
		}
		return m(
			'text',
			{
				className: ClassNames.label.text,
				style: Style[ClassNames.label.text],
				y: '1em',
				x: `0`,
				width: options.scale * LABEL_SIZE.width,
				'text-anchor': 'middle',
				dy: '0.25em',
				'c-Draw-Label': IkeDrawLabel.LABEL,
				key: offset,
			},
			outputLine
		);
	}

	static ellipsisText({ dom }) {
		const parentWidth = LABEL_SIZE.width * 0.9;

		for (let i = 0; i < dom.childNodes.length; i++) {
			const textEl = dom.childNodes[i];
			if (textEl.nodeType !== ELEMENT_NODE) {
				continue;
			}

			const textNode = textEl.firstChild;
			if (textNode == null) {
				continue;
			}
			// Skip short lines
			if (textNode.textContent.length < ELLIPSIS_LINE_LENGTH) {
				continue;
			}

			// Skip text that has already been processed
			if (textNode.textContent.indexOf('…') > -1) {
				continue;
			}

			if (textNode.$lastText === textNode.textContent) {
				continue;
			}

			let computedLength = getComputedTextLengthSafe(textEl) || DEFAULT_CHAR_WIDTH * textNode.textContent.length;
			const addDots = computedLength > parentWidth;

			// If text lines are really long, just assume we will need to truncate it
			if (textNode.textContent.length > ELLIPSIS_LINE_MAX_LENGTH) {
				const textSizePerCharacter = computedLength / textNode.textContent.length;
				const maxNumberOfChars = Math.floor(parentWidth / textSizePerCharacter);
				textNode.textContent = textNode.textContent.slice(0, maxNumberOfChars);
			}

			while (computedLength > parentWidth) {
				textNode.textContent = textNode.textContent.slice(0, textNode.textContent.length - 2);
				computedLength = getComputedTextLengthSafe(textEl) || DEFAULT_CHAR_WIDTH * textNode.textContent.length;
			}

			if (addDots && textNode.textContent.indexOf('…') === -1) {
				textNode.textContent = textNode.textContent + '…';
			} else {
				textNode.$lastText = textNode.textContent;
			}
		}
	}

	static deleteButton(options: SVGRenderOptions) {
		const radius = options.scale * LABEL_ACCEPT_AND_DELETE_RADIUS;
		const XSize = radius / 2;
		const y = radius * 1.25;
		const x = options.scale * LABEL_SIZE.width - radius * 1.25;
		const strokeWidth = options.scale * StyleConfig.STROKE_WIDTH;

		const X1 = m('line', {
			x1: x - XSize,
			y1: y - XSize,
			x2: x + XSize,
			y2: y + XSize,
			stroke: StyleConfig.BUTTON.DELETE_STROKE,
			'stroke-width': strokeWidth,
			'pointer-events': 'none',
		});

		const X2 = m('line', {
			x1: x + XSize,
			y1: y - XSize,
			x2: x - XSize,
			y2: y + XSize,
			stroke: StyleConfig.BUTTON.DELETE_STROKE,
			'stroke-width': strokeWidth,
			'pointer-events': 'none',
		});

		const circle = m('circle', {
			cx: x,
			cy: y,
			r: radius,
			fill: '#fafafa',
			'c-Draw-Label': IkeDrawLabel.DELETE,
		});

		return m(
			'g',
			{
				className: ClassNames.label.delete,
				transform: `translate(${(-8 * options.scale) / 4}, ${(5 * options.scale) / 4})`,
			},
			[circle, X1, X2]
		);
	}

	static acceptButton(options: SVGRenderOptions) {
		const radius = options.scale * LABEL_ACCEPT_AND_DELETE_RADIUS;
		const checkSymbolSize = radius / 2;
		const y = radius * 1.25;
		const x = radius * 1.25;
		const strokeWidth = options.scale * StyleConfig.STROKE_WIDTH;
		const shortStroke = m('line', {
			x1: x - checkSymbolSize,
			y1: y,
			x2: x - checkSymbolSize * 0.5 + strokeWidth * 0.5,
			y2: y + checkSymbolSize * 0.75,
			stroke: StyleConfig.BUTTON.ACCEPT_STROKE,
			'stroke-width': strokeWidth,
			'pointer-events': 'none',
		});

		const longStroke = m('line', {
			x1: x - checkSymbolSize * 0.5,
			y1: y + checkSymbolSize * 0.75,
			x2: x + checkSymbolSize,
			y2: y - checkSymbolSize,
			stroke: StyleConfig.BUTTON.ACCEPT_STROKE,
			'stroke-width': strokeWidth,
			'pointer-events': 'none',
		});

		const circle = m('circle', {
			cx: x,
			cy: y,
			r: radius,
			fill: '#fafafa',
			'c-Draw-Label': IkeDrawLabel.ACCEPT,
		});

		return m(
			'g',
			{
				className: ClassNames.label.accept,
				transform: `translate(${(8 * options.scale) / 4}, ${(5 * options.scale) / 4})`,
			},
			[circle, shortStroke, longStroke]
		);
	}
}
